export const CENTINELA_UNIT_STATUSES = [
  "safe",
  "warning",
  "alert",
  "risk",
  "offline",
  "sin_custodia",
] as const;

export type CentinelaUnitStatus = (typeof CENTINELA_UNIT_STATUSES)[number];

export const CENTINELA_MESSAGE_TONES = ["info", "warn", "alert"] as const;

export type CentinelaMessageTone = (typeof CENTINELA_MESSAGE_TONES)[number];

export type CentinelaStatusCounts = Record<CentinelaUnitStatus, number>;

export interface CentinelaChip {
  label: string;
  type: string;
}

export interface CentinelaPosition {
  lat?: number | null;
  lng?: number | null;
  speed_kph?: number | null;
  heading?: number | null;
}

export interface CentinelaUnitTrip {
  trip_id?: string | null;
  name?: string | null;
  status?: string | null;
}

export interface CentinelaUnit {
  device_id: string;
  name: string | null;
  plate: string | null;
  driver: string | null;
  has_operator_phone: boolean;
  under_custody: boolean;
  status: CentinelaUnitStatus;
  ai_message: string | null;
  ai_message_type: CentinelaMessageTone;
  chips: CentinelaChip[];
  last_position_at: string | null;
  last_update_at: string | null;
  position: CentinelaPosition | null;
  trip: CentinelaUnitTrip | null;
  open_case_id: string | null;
}

export interface CentinelaUnitsResponse {
  demo?: boolean;
  counts: CentinelaStatusCounts;
  units: CentinelaUnit[];
}

export interface CentinelaUnitResponse {
  unit: CentinelaUnit;
}

export interface CentinelaNarrative {
  titulo: string;
  cuerpo: string;
  texto: string;
  tono: string;
}

export interface CentinelaPanelAlerts {
  disponible?: boolean;
  motivo?: string | null;
  total: number;
  por_dia: { dia: string; alertas: number }[];
}

export interface CentinelaPanelKilometers {
  disponible?: boolean;
  motivo?: string | null;
  total_km: number;
  por_dia: { dia: string; km: number }[];
}

export interface CentinelaPanelTrips {
  total: number;
  en_curso: number;
  programado: number;
  completado: number;
  cancelado: number;
  sin_estado: number;
}

export interface CentinelaPanelPerformance {
  verificaciones: { total: number; exitosas: number; pct: number | null };
  reaccion_seg: number | null;
  incidentes_reales: number;
}

export interface CentinelaPanelCustody {
  trip_id: string;
  device_id: string;
  titulo: string | null;
  perfil: string | null;
  iniciado_en: string | null;
  termina_en: string | null;
  avance_pct: number | null;
  avance_es_estimado: boolean;
}

export interface CentinelaSummaryPanel {
  ventana_dias: number;
  calculado_en: string | null;
  alertas_historicas: CentinelaPanelAlerts;
  kilometros: CentinelaPanelKilometers;
  viajes: CentinelaPanelTrips;
  rutas_disponibles: number;
  rendimiento: CentinelaPanelPerformance;
  custodias: CentinelaPanelCustody[];
}

export interface CentinelaSummary {
  company_id?: string;
  generated_at?: string | null;
  demo?: boolean;
  window_hours: number;
  units: CentinelaStatusCounts & { total: number };
  cases: {
    total: number;
    open: number;
    resolved_without_call: number;
    requires_human: number;
  };
  watching: { geofences: number; trips: number };
  narrativa: CentinelaNarrative | null;
  calls: { total: number; answered: number; answer_rate: number | null };
  panel?: CentinelaSummaryPanel;
}

export const CENTINELA_TRANSCRIPT_ROLES = ["ia", "operador", "supervisor"] as const;

export type CentinelaTranscriptRole = (typeof CENTINELA_TRANSCRIPT_ROLES)[number];

export interface CentinelaTranscriptLine {
  role: CentinelaTranscriptRole;
  speaker?: string | null;
  text: string;
  at: string;
}

export interface CentinelaFinding {
  tipo: string;
  nota: string | null;
}

export interface CentinelaCall {
  id: string;
  case_id: string | null;
  device_id: string;
  call_type: string;
  profile: string | null;
  to_phone_masked: string | null;
  status: string;
  started_at: string;
  answered_at: string | null;
  ended_at: string | null;
  duration_sec: number;
  keyword_result: string | null;
  duress_detected: boolean;
  findings: CentinelaFinding[];
  has_recording: boolean;
  transcript: CentinelaTranscriptLine[];
}

export interface CentinelaCallsResponse {
  calls: CentinelaCall[];
}

export interface CentinelaCallRecording {
  disponible: boolean;
  url: string | null;
  tipo?: string;
  duracion_sec?: number;
  motivo?: string | null;
  expira_en?: string | null;
}

export interface CentinelaCallRecordingResponse {
  call_id: string;
  recording: CentinelaCallRecording;
}

/** Una foto o un clip que la cámara subió alrededor del hecho. */
export interface CentinelaCaseMedia {
  /** Epoch ms del momento en que la cámara lo grabó. */
  ts: number;
  filename: string | null;
  /** "video" para los clips, "foto" para las capturas. */
  kind: string | null;
  /** Como lo nombra el equipo: "front" (carretera) o "inward" (cabina). */
  camera: string | null;
  event_code: number | null;
  size_bytes?: number | null;
  /** Firmada y temporal: el bucket es privado. */
  url: string;
  /** La ruta en el bucket: identidad estable del archivo. El nombre no sirve,
   *  la cámara lo reusa al retransmitir. */
  object?: string | null;
  /** Sospecha AUTOMÁTICA de que no muestra nada. No es un veredicto. */
  util?: boolean;
  motivo_inutil?: string | null;
  /** Alguien lo marcó a mano. Esto sí es un veredicto. */
  descartada?: boolean;
}

export interface CentinelaCaseMediaResponse {
  media: CentinelaCaseMedia[];
  total: number;
}

export interface CentinelaCase {
  id: string;
  company_id: string;
  device_id: string;
  vehicle_id: string | null;
  trip_id: string | null;
  device_name: string | null;
  vehicle_name: string | null;
  kind: string;
  alert_type_id: string | null;
  status: string;
  urgency: string;
  severity: string;
  opened_at: string;
  closed_at: string | null;
  resolution: string | null;
  requires_human: boolean;
  theft_score: number;
  ai_summary: string | null;
  /** El hecho CON números: «Iba a 118 km/h, con el límite en 100». */
  alert_label?: string | null;
  kind_label?: string | null;
  /** Dónde ocurrió, en palabras. `null` si no se supo. */
  donde?: string | null;
  /** Cuánto lleva abierto, o cuánto duró si ya cerró. */
  duracion_min?: number | null;
  /** Por qué importa, del catálogo de alertas. */
  por_que?: string | null;
  calls_count: number;
  driver_contact_attempts: number;
  unit_status: CentinelaUnitStatus | null;
}

export interface CentinelaTimelineStep {
  step_index: number;
  channel: string;
  label: string;
  detail: string | null;
  status: string;
  executed_at: string;
  recipient_id: string | null;
  recipient_name: string | null;
  recipient_role: string | null;
}

export interface CentinelaEvent {
  case: CentinelaCase;
  steps: CentinelaTimelineStep[];
}

export interface CentinelaEventsResponse {
  events: CentinelaEvent[];
}

/**
 * Una entrada de la bitácora: un hecho que NO abrió caso.
 *
 * Existe aparte de `CentinelaEvent` porque son cosas distintas. Los tipos con
 * `verification_policy: solo_registro` —nt-31 conducta al volante, nt-32
 * asistencia a la conducción— se anotan y nada más, así que por más que la
 * cámara los reporte nunca aparecen en la lista de casos.
 */
export interface CentinelaBitacoraEntry {
  id: string | null;
  /** La hora del HECHO, no la de escritura. */
  at: string | null;
  alert_type_id: string | null;
  label: string | null;
  signal: string | null;
  /** El valor crudo de la señal: lo que separa un frenazo de uno fuerte. */
  value: number | string | null;
  source: string | null;
  lat: number | null;
  lon: number | null;
  speed_kmh: number | null;
  geocerca: string | null;
}

/** Cuándo subió algo esta cámara por última vez, mire quien mire qué día. */
export interface CentinelaUltimaSubida {
  /** El día, en YYYY-MM-DD. */
  fecha: string;
  /** La hora exacta del último archivo. `null` si el día quedó registrado vacío. */
  ts: number | null;
  archivos: number;
}

export interface CentinelaUnitMediaResponse {
  media: CentinelaCaseMedia[];
  total: number;
  /** Viene cuando se pidió una ventana móvil. */
  horas?: number;
  /** Viene cuando se pidió un día concreto. */
  fecha?: string;
  /**
   * Para que un día vacío no se confunda con una cámara rota.
   *
   * `null` si esta unidad nunca subió nada.
   */
  ultima?: CentinelaUltimaSubida | null;
}

/** Una conducta al volante vista por la cámara, agrupada por tipo. */
export interface CentinelaConducta {
  signal: string;
  etiqueta: string;
  veces: number;
  primera_hora: string;
  ultima_hora: string;
}

/**
 * Cuidado con las horas: el parte llega con `hora` ya formateada en la zona de
 * la empresa (`"14:38"`), NO con epoch. Es deliberado — el día se corta en la
 * zona del cliente y formatearlo en el navegador movería los hechos de lugar
 * para quien mire desde otro huso. La primera versión de estos tipos declaró
 * `at: number` y la línea de tiempo salió con renglones en blanco: el campo
 * nunca existió.
 */
export interface CentinelaMovimiento {
  hora: string;
  /** Qué hizo, ya en palabras: "salió de casa", "se detuvo". */
  que: string;
  donde: string | null;
  lat: number | null;
  lon: number | null;
}

export interface CentinelaIncidencia {
  hora: string;
  que: string;
  estado: string;
  desenlace: string;
  llamadas: number;
}

export interface CentinelaConversacion {
  hora: string;
  tipo: string;
  contesto: boolean;
  dijo: string | null;
  hallazgo: string | null;
  duracion_seg: number;
}

/**
 * La medición del día: kilómetros y en qué se fue el tiempo.
 *
 * `ralenti_min` es `null` cuando el equipo nunca reportó ignición: «no sé» y
 * «no hubo» son cosas distintas, y un cero ahí sería una afirmación falsa.
 * `sin_datos_min` es el tiempo que los huecos de cobertura dejan sin clasificar
 * — se expone para que los totales no se lean como el día completo.
 */
export interface CentinelaTramo {
  desde: number;
  hasta: number;
  estado: "conduciendo" | "trafico" | "detenido" | "ralenti" | "sin_datos";
  /** Dónde empezó el tramo. `null` si ese punto no traía coordenadas. */
  lat: number | null;
  lon: number | null;
  /** De la caché de direcciones de wayak-realtime. `null` si esa coordenada
   *  todavía no se había geocodificado — se enseñan las coordenadas. */
  direccion?: string | null;
}

export interface CentinelaJornada {
  /** Cuándo pasó cada cosa, para la regla de 24 horas. Los totales dicen
   *  cuánto; esto dice a qué hora, que es otra pregunta. */
  tramos: CentinelaTramo[];
  km: number;
  conduciendo_min: number;
  /** Subconjunto de `conduciendo_min`: tiempo a vuelta de rueda. */
  trafico_min: number;
  detenido_min: number;
  ralenti_min: number | null;
  /** Inferido de la forma de las paradas, NO medido: una parada corta metida
   *  entre dos tramos de manejo es casi siempre motor encendido. Vive aparte de
   *  `ralenti_min` para que una suposición nunca se presente como un dato. */
  ralenti_estimado_min: number;
  descanso_min: number;
  sin_datos_min: number;
  puntos: number;
  primera: number | null;
  ultima: number | null;
}

/** Lo que Centinela ya aprendió de la unidad. */
export interface CentinelaCostumbres {
  suele_volver: string | null;
  suele_salir: string | null;
  aprendiendo: { faltan: number } | null;
  lugares_frecuentes: number;
  /** Los lugares en sí, ordenados por visitas. Un contador sin la lista es un
   *  dato que no se puede usar. */
  lugares?: {
    lat: number;
    lon: number;
    veces: number;
    ultima_vez: number | null;
    direccion?: string | null;
  }[];
  ultima_confirmacion: { hora: string; dijo: string; donde: string } | null;
}

export interface CentinelaParte {
  unidad: { device_id: string | null; nombre: string | null; placa: string | null; operador: string | null };
  movimientos: CentinelaMovimiento[];
  incidencias: CentinelaIncidencia[];
  conversaciones: CentinelaConversacion[];
  resumen: {
    movimientos: number;
    incidencias: number;
    abiertas: number;
    llamadas: number;
    sin_contestar: number;
    conductas: number;
  };
  /** Vacío en una unidad sin cámara: el resto del parte sigue siendo útil. */
  conductas: CentinelaConducta[];
  /** Lo medido recorriendo las posiciones del día. `null` cuando no se pudo
   *  leer el histórico — que NO es lo mismo que una jornada en ceros. */
  jornada: CentinelaJornada | null;
  costumbres: CentinelaCostumbres | null;
  narrativa: string;
  /** Quién escribió el resumen. `plantilla` significa que Centinela no pudo
   *  —sin key, API caída, tardó de más— y salió el texto determinista. */
  narrativa_origen?: "ia" | "cache" | "plantilla";
}

export interface CentinelaParteResponse {
  fecha: string;
  timezone: string;
  parte: CentinelaParte;
}

export interface CentinelaBitacoraResponse {
  entradas: CentinelaBitacoraEntry[];
  total: number;
  desde: string;
  hasta: string;
  timezone: string;
}

export interface CentinelaCasesResponse {
  cases: CentinelaCase[];
  total?: number;
}

export interface CentinelaCasesQuery {
  status?: string;
  kind?: string;
  device_id?: string;
  from?: string;
  to?: string;
  limit?: number;
}

export interface CentinelaAlertParam {
  key: string;
  unit: string;
  default: number;
  options: number[];
  maxAttempts?: { default: number; options: number[] };
}

export const CENTINELA_ALERT_SCOPES = ["ambos", "global", "viaje"] as const;

export type CentinelaAlertScope = (typeof CENTINELA_ALERT_SCOPES)[number];

export interface CentinelaAlertType {
  id: string;
  label: string;
  description: string;
  signals: string[];
  verification_policy: string;
  call_type: string | null;
  scope: CentinelaAlertScope;
  enabled_default: boolean;
  escalate_in_parallel?: boolean;
  param: CentinelaAlertParam | null;
}

export interface CentinelaAlertState {
  id: string;
  label: string;
  enabled: boolean;
  valor: number | null;
  param: CentinelaAlertParam | null;
  verification_policy: string;
  call_type: string | null;
}

export interface CentinelaAlertPatchInput {
  enabled?: boolean;
  [param: string]: boolean | number | undefined;
}

export interface CentinelaAlertPatchResponse {
  alert_type: CentinelaAlertState;
}

export const CENTINELA_CALL_SEVERITIES = ["verde", "ámbar", "rojo", "teal"] as const;

export type CentinelaCallSeverity = (typeof CENTINELA_CALL_SEVERITIES)[number];

export const CENTINELA_KEYWORD_MODES = ["requerida", "opcional", "ninguna"] as const;

export type CentinelaKeywordMode = (typeof CENTINELA_KEYWORD_MODES)[number];

const CALL_SEVERITY_ALIASES: Record<string, CentinelaCallSeverity> = {
  ambar: "ámbar",
};

export const toCallSeverity = (
  value: string | null | undefined,
): CentinelaCallSeverity | null => {
  if (!value) return null;
  const normalized = CALL_SEVERITY_ALIASES[value] ?? value;
  return (CENTINELA_CALL_SEVERITIES as readonly string[]).includes(normalized)
    ? (normalized as CentinelaCallSeverity)
    : null;
};

export const CENTINELA_CALL_PROFILES = ["operador", "supervisor"] as const;

export type CentinelaCallProfile = (typeof CENTINELA_CALL_PROFILES)[number];

export interface CentinelaCallType {
  id: string;
  label: string;
  severity: string | null;
  profile: CentinelaCallProfile;
  keyword_default: CentinelaKeywordMode | false;
  objective: string;
}

export interface CentinelaSituation {
  label: string;
  unit_status: CentinelaUnitStatus;
  notify_supervisor: boolean;
  requires_human: boolean;
  implies_delay: boolean;
  justifies_stop: boolean;
  justifies_deviation: boolean;
}

export interface CentinelaChannel {
  id: string;
  label: string;
  configurable: boolean;
}

export interface CentinelaCustodyParameter {
  campo: CentinelaCustodyThreshold;
  etiqueta: string;
  unidad: string;
  descripcion: string;
}

export type CentinelaCustodyProfile = Partial<
  Record<CentinelaCustodyThreshold, number>
> & {
  id: string;
  nombre: string;
  descripcion: string;
  alertas: string[] | null;
  llamadas_estimadas_por_4h: number | null;
  libera_selectores?: boolean;
};

export interface CentinelaCustodyProfiles {
  parametros: CentinelaCustodyParameter[];
  perfiles: CentinelaCustodyProfile[];
}

export interface CentinelaCatalogs {
  alert_types: CentinelaAlertType[];
  call_types: CentinelaCallType[];
  situations: CentinelaSituation[];
  channels: CentinelaChannel[];
  custody_profiles: CentinelaCustodyProfiles;
  call_actions: string[];
  unsupported: {
    channels: string[];
    actions: string[];
    reason: string;
  };
}

export const CENTINELA_SUPERVISOR_EVENTS = [
  "verificacion_ok",
  "problema_reportado",
  "verificacion_fallida",
  "informativas",
] as const;

export type CentinelaSupervisorEvent = (typeof CENTINELA_SUPERVISOR_EVENTS)[number];

export type CentinelaSupervisorNotices = Record<CentinelaSupervisorEvent, boolean> & {
  coaccion: boolean;
};

export interface CentinelaQuietHours {
  enabled: boolean;
  from: string;
  to: string;
}

export interface CentinelaEscalation {
  driver_first: boolean;
  max_driver_attempts: number;
  attempt_delay_min: number;
  notify_supervisor_on: CentinelaSupervisorNotices;
  supervisor_quiet_hours: CentinelaQuietHours;
}

export interface CentinelaOperationSchedule {
  desde: string;
  hasta: string;
  timezone: string;
}

export interface CentinelaVoiceConfig {
  voice_id: string;
}

export interface CentinelaConfig {
  escalation: CentinelaEscalation;
  ajuste_de_ruta: { enabled: boolean };
  horario_operacion: CentinelaOperationSchedule;
  voice: CentinelaVoiceConfig;
}

export type CentinelaConfigInput = {
  escalation?: Partial<
    Omit<CentinelaEscalation, "notify_supervisor_on" | "supervisor_quiet_hours">
  > & {
    notify_supervisor_on?: Partial<CentinelaSupervisorNotices>;
    supervisor_quiet_hours?: Partial<CentinelaQuietHours>;
  };
  ajuste_de_ruta?: { enabled: boolean };
  horario_operacion?: Partial<CentinelaOperationSchedule>;
  voice?: Partial<CentinelaVoiceConfig>;
};

export interface CentinelaKeywordPolicy {
  enabled: boolean;
  mode: string;
  day_word: string;
  duress_word: string;
  rotation: string;
}

export interface CentinelaUnitConfig {
  device_id: string;
  operator_name: string;
  operator_phone: string;
  operator_phone_masked: string;
  has_operator_phone: boolean;
  enabled: boolean;
  custody_profile: string;
  keyword_policy: CentinelaKeywordPolicy;
}

export type CentinelaUnitConfigInput = {
  operator_name?: string;
  operator_phone?: string;
  enabled?: boolean;
  keyword_policy?: Partial<CentinelaKeywordPolicy>;
};

export interface CentinelaTripCustody {
  profile: string;
  verification_interval_min: number;
  speed_limit_kmh: number;
  max_stop_min: number;
  gps_timeout_min: number;
  routine_call_interval_min: number;
  notify_user_ids: string[];
}

export const CENTINELA_CUSTODY_THRESHOLDS = [
  "verification_interval_min",
  "speed_limit_kmh",
  "max_stop_min",
  "gps_timeout_min",
  "routine_call_interval_min",
] as const;

export type CentinelaCustodyThreshold = (typeof CENTINELA_CUSTODY_THRESHOLDS)[number];

export type CentinelaTripCustodyInput = Partial<CentinelaTripCustody>;

export const CENTINELA_CUSTODY_EXPIRY_UNITS = ["horas", "dias", "meses", "anios"] as const;

export type CentinelaCustodyExpiryUnit = (typeof CENTINELA_CUSTODY_EXPIRY_UNITS)[number];

export type CentinelaCustodyExpiry =
  | { unidad: CentinelaCustodyExpiryUnit; cantidad: number }
  | { unidad: "permanente" };

export const CENTINELA_UNIT_CUSTODY_THRESHOLDS = [
  "speed_limit_kmh",
  "gps_timeout_min",
] as const satisfies readonly CentinelaCustodyThreshold[];

export type CentinelaUnitCustodyThreshold =
  (typeof CENTINELA_UNIT_CUSTODY_THRESHOLDS)[number];

export interface CentinelaUnitCustody {
  device_id: string;
  activa: boolean;
  vigente: boolean;
  permanente: boolean;
  expira_en: string | null;
  expira_en_min: number | null;
  expiracion: CentinelaCustodyExpiry | null;
  alertas_no_aplicables: string[];
  profile: string;
  speed_limit_kmh: number;
  gps_timeout_min: number;
  notify_user_ids: string[];
}

export interface CentinelaUnitCustodyInput {
  expiracion: CentinelaCustodyExpiry;
  activa: boolean;
  profile?: string;
  speed_limit_kmh?: number;
  gps_timeout_min?: number;
  notify_user_ids?: string[];
}

export const CENTINELA_USAGE_APPLICATIONS = ["soft", "hard", "metric"] as const;

export type CentinelaUsageApplication = (typeof CENTINELA_USAGE_APPLICATIONS)[number];

export interface CentinelaUsageProjection {
  cierre_estimado: number;
  avance_periodo_pct: number;
  confiable: boolean;
}

export interface CentinelaUsageMetric {
  kind: string;
  etiqueta: { singular: string; plural: string };
  usado: number;
  tope: number | null;
  tope_origen: string | null;
  todo_facturable: boolean;
  restante: number | null;
  pct: number | null;
  extra: number;
  importe_extra: number;
  precio_unitario: number;
  aplicacion: CentinelaUsageApplication;
  cobrable: boolean;
  aviso: string | null;
  proyeccion: CentinelaUsageProjection | null;
}

export interface CentinelaUsageAmount {
  base: number;
  extra: number;
  total: number;
  proyectado_cierre: number;
}

export interface CentinelaUsageBlock {
  company_id: string;
  period: string;
  modelo: string;
  moneda: string;
  precio_base: number;
  compromiso: number | null;
  avance_periodo_pct: number;
  protagonista: CentinelaUsageMetric | null;
  secundarios: CentinelaUsageMetric[];
  importe: CentinelaUsageAmount;
}

export interface CentinelaUsageGroup {
  id: string;
  etiqueta: string;
  descripcion: string;
  llamadas: number;
  pct: number;
}

export interface CentinelaUsageByType {
  tipo: string;
  llamadas: number;
  grupo: string;
}

export interface CentinelaUsageVoice {
  minutos_totales: number;
  promedio_por_llamada: string;
  por_dia: { dia: string; minutos: number }[];
}

export interface CentinelaUsageTopUnit {
  device_id: string;
  nombre: string;
  llamadas: number;
  tiempo: string;
  pct: number;
}

export interface CentinelaUsageBreakdown {
  total: number;
  grupos: CentinelaUsageGroup[];
  por_tipo: CentinelaUsageByType[];
  voz: CentinelaUsageVoice | null;
  top_unidades: CentinelaUsageTopUnit[];
}

export interface CentinelaUsageResponse {
  usage: CentinelaUsageBlock;
  desglose: CentinelaUsageBreakdown;
  previsualizacion: unknown;
  recommendations: unknown[];
}

export interface CentinelaResourcePlan {
  incluido: number | null;
  precio_unitario: number;
  aplicacion: CentinelaUsageApplication;
}

export interface CentinelaLicenseLevel {
  id: string;
  llamadas_incluidas: number;
  geocercas: number | null;
  viajes: number | null;
  precio_unidad_mes: number;
  precio_llamada_extra: number | null;
}

export interface CentinelaCallPackage {
  llamadas: number;
  precio: number;
  unitario: number;
}

export interface CentinelaSubscriptionPlan {
  model: string;
  level: string | null;
  currency: string;
  base_price: number;
  resources: Record<string, CentinelaResourcePlan>;
  unconfigured: boolean;
}

export interface CentinelaSubscriptionCatalog {
  models: string[];
  license_levels: CentinelaLicenseLevel[];
  call_packages: CentinelaCallPackage[];
}

export interface CentinelaSubscriptionResponse {
  subscription: CentinelaSubscriptionPlan;
  catalog: CentinelaSubscriptionCatalog;
}

export interface CentinelaTestCallInput {
  telefono: string;
  tipo: string;
  nombre?: string;
  unidad?: string;
  perfil?: string;
}

export interface CentinelaTestNotificationInput {
  canales: string[];
  user_id?: string;
  telefono?: string;
}

export interface CentinelaTestNotificationResponse {
  contenido?: Record<string, unknown>;
}

export const CENTINELA_SCHEDULE_DAYS = ["D", "L", "M", "X", "J", "V", "S"] as const;

export type CentinelaScheduleDay = (typeof CENTINELA_SCHEDULE_DAYS)[number];

export interface CentinelaSchedule {
  activo: boolean;
  dias: CentinelaScheduleDay[];
  desde: string;
  hasta: string;
  timezone?: string;
}

export interface CentinelaUserNotifications {
  habilitado: boolean;
  canales: string[];
  tipos: string[];
  horario?: CentinelaSchedule;
  actualizado_en?: string;
}

export interface CentinelaUser {
  user_id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  role_id: string | null;
  activo: boolean;
  canales_posibles: string[];
  sin_telefono: string | null;
  notificaciones: CentinelaUserNotifications;
}

export interface CentinelaNotificationChannel {
  id: string;
  label: string;
  requiere: string | null;
}

export interface CentinelaUsersResponse {
  demo?: boolean;
  users: CentinelaUser[];
  canales: CentinelaNotificationChannel[];
}

export type CentinelaUserNotificationsInput = CentinelaUserNotifications;

export const CENTINELA_SHIFT_REASONS = [
  "en_turno",
  "fuera_de_turno",
  "sin_horario",
] as const;

export type CentinelaShiftReason = (typeof CENTINELA_SHIFT_REASONS)[number];

export interface CentinelaSupervisor {
  user_id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  puede_recibir_llamadas: boolean;
  habilitado: boolean;
  canales: string[];
  tipos: string[];
  horario: CentinelaSchedule;
  en_turno_ahora: boolean;
  motivo: string;
}

export interface CentinelaSupervisoresResponse {
  demo?: boolean;
  supervisores: CentinelaSupervisor[];
  dias: CentinelaScheduleDay[];
}

export interface CentinelaVoiceTuning {
  speed: number;
  stability: number;
  similarity: number;
}

export interface CentinelaVoice {
  id: string;
  nombre: string;
  genero: string;
  se_presenta_como?: string;
  ajuste?: CentinelaVoiceTuning;
}

export interface CentinelaVocesResponse {
  demo?: boolean;
  actual: CentinelaVoice | null;
  disponibles: CentinelaVoice[];
}

export interface CentinelaVozInput {
  voz_id: string;
  speed?: number;
  stability?: number;
  similarity?: number;
}

export interface CentinelaVozResponse {
  actual: CentinelaVoice | null;
  nota?: string;
}

export interface CentinelaUnitCallInput {
  tipo: string;
  hecho?: string;
  motivo?: string;
}

export interface CentinelaUnitCallResponse {
  call_sid?: string;
  session_id?: string;
  case_id?: string;
  tipo?: string;
  origen?: string;
  device_id?: string;
}

export interface CentinelaNumero {
  numero: string;
  etiqueta: string | null;
  pais: string | null;
  sms: boolean;
}

export interface CentinelaNumerosResponse {
  demo?: boolean;
  actual: string | null;
  por_defecto: string | null;
  disponibles: CentinelaNumero[];
}

export interface CentinelaNumeroInput {
  numero_monitorista: string | null;
}

export interface CentinelaGeofenceSuggestionPoint {
  lat?: number | null;
  lon?: number | null;
}

export interface CentinelaGeofenceCorridor {
  distancia_km?: number | null;
  muestras?: number | null;
  origen?: CentinelaGeofenceSuggestionPoint | null;
  destino?: CentinelaGeofenceSuggestionPoint | null;
}

export interface CentinelaGeofenceSource {
  url?: string | null;
  titulo?: string | null;
}

export interface CentinelaGeofenceSuggestion {
  nombre?: string | null;
  tipo?: string | null;
  centro?: { latitude?: number | null; longitude?: number | null } | null;
  radio_m?: number | null;
  motivo?: string | null;
  confianza?: string | null;
  fuentes?: CentinelaGeofenceSource[] | null;
  origen?: string | null;
  distancia_a_ruta_km?: number | null;
}

export interface CentinelaGeofenceResearch {
  corrida?: boolean | null;
  busquedas?: number | null;
  fuentes?: CentinelaGeofenceSource[] | null;
}

export interface CentinelaGeofenceCost {
  usd?: number | null;
  tokens_entrada?: number | null;
  tokens_salida?: number | null;
  busquedas?: number | null;
  segundos?: number | null;
}

export interface CentinelaGeofenceSuggestionsResult {
  ok?: boolean | null;
  corredor?: CentinelaGeofenceCorridor | null;
  sugerencias?: CentinelaGeofenceSuggestion[] | null;
  limite?: number | null;
  descartadas?: unknown[] | null;
  investigacion?: CentinelaGeofenceResearch | null;
  costo?: CentinelaGeofenceCost | null;
  creadas?: number | null;
}

export interface CentinelaGeofenceSuggestionsStart {
  job_id?: string | null;
  estado?: string | null;
  reusado?: boolean | null;
  corredor?: CentinelaGeofenceCorridor | null;
  consultar?: string | null;
  detalle?: string | null;
}

export interface CentinelaGeofenceSuggestionsJob {
  id?: string | null;
  company_id?: string | null;
  route_id?: string | null;
  estado?: string | null;
  iniciado_en?: string | null;
  terminado_en?: string | null;
  resultado?: CentinelaGeofenceSuggestionsResult | null;
  error?: unknown;
}

/**
 * Un hecho del día, ya ubicado en el mapa.
 *
 * `ubicado: false` significa que no se pudo saber dónde ocurrió y por eso no
 * trae lat/lon — no que no haya pasado. `ubicacion_interpolada` avisa que la
 * posición se dedujo del recorrido por cercanía en el tiempo y no la midió el
 * equipo en ese instante; importa para no dibujar un pin con más precisión de
 * la que tiene.
 */
export interface CentinelaHecho {
  ts: number;
  hora: string;
  origen: "bitacora" | "caso";
  tipo: "conducta" | "movimiento" | "caso" | "registro";
  signal: string | null;
  etiqueta: string;
  severidad: string;
  lat: number | null;
  lon: number | null;
  ubicado: boolean;
  ubicacion_interpolada: boolean;
  geocerca: string | null;
  direccion?: string | null;
  /** La evidencia que la cámara grabó alrededor del hecho. Vacío no significa
   *  que no pasó: significa que no hubo archivo cerca en el tiempo. */
  media?: CentinelaHechoMedia[];
  velocidad_kmh: number | null;
  estado?: string;
  caso_id: string | null;
}

/** Un archivo colgado de un hecho. */
export interface CentinelaHechoMedia {
  ts: number;
  kind: string | null;
  camera: string | null;
  url: string;
  /** `false` cuando el sistema sospecha que no muestra nada —una foto de pocos
   *  kilobytes suele ser la pantalla de arranque de la cámara de cabina—. Es
   *  una sospecha, no un veredicto: la decisión de descartar es de una persona. */
  util: boolean;
  motivo_inutil: string | null;
  /** Segundos entre el archivo y el hecho. */
  desfase_seg: number;
}

export interface CentinelaPuntoRuta {
  ts: number;
  lat: number;
  lon: number;
  speed: number | null;
}

/**
 * El día de conducción completo. Un solo contrato para el mapa, la tarjeta de
 * la unidad y los reportes.
 *
 * `recorrido` llega en `null` salvo que se pida con `incluir=recorrido`: la
 * traza de un día son miles de puntos y sólo el mapa los necesita.
 */
export interface CentinelaConduccion {
  unidad: { device_id: string | null; nombre: string | null; placa: string | null; operador: string | null };
  medicion: CentinelaJornada | null;
  conductas: CentinelaConducta[];
  resumen: {
    movimientos: number;
    incidencias: number;
    abiertas: number;
    llamadas: number;
    sin_contestar: number;
    conductas: number;
  };
  hechos: CentinelaHecho[];
  sin_ubicar: number;
  con_evidencia: number;
  recorrido: CentinelaPuntoRuta[] | null;
  recorrido_puntos: number;
  /** Null si la unidad no trae un equipo que mida el motor. */
  telemetria?: CentinelaTelemetria | null;
  llamadas: CentinelaConversacion[];
  costumbres: CentinelaCostumbres | null;
  narrativa: string;
  narrativa_origen?: "ia" | "cache" | "plantilla";
}

/**
 * La ventana que el servidor realmente contesto.
 *
 * Va explicita porque `fecha` sola dejo de describirla: con `?horas=N` el
 * rango se cuenta desde ahora y puede cruzar la medianoche, asi que la fecha
 * es la del final y no dice donde empieza.
 */
export interface CentinelaVentana {
  desde: number;
  hasta: number;
  /** Null cuando se pidio un dia natural. */
  horas: number | null;
  tipo: "dia" | "movil";
}

/**
 * El equipo companero: un segundo aparato en el MISMO vehiculo que aporta lo
 * que el principal no mide —encendido, rpm, temperatura, voltaje—.
 *
 * `divergencia` no es opcional por capricho: el emparejamiento se configura una
 * vez y nadie se acuerda de quitarlo si mueven el equipo a otro camion. Cuando
 * los dos se separan de verdad, el servidor deja de mezclar y lo reporta aqui
 * para que la pantalla lo diga en vez de callarlo.
 */
/**
 * Lo que el motor dijo, cuando la unidad trae un equipo que lo mide.
 *
 * `ultima` es el estado actual —lo que contesta «como esta ahora»— y `extremos`
 * lo peor del dia: el pico de revoluciones, el sobrecalentamiento, el voltaje
 * que se cayo. Van resumidos y no punto por punto porque un dia son cientos de
 * miles de valores para acabar enseniando un puniado de numeros.
 *
 * Es `null` en una unidad sin equipo companiero. Un objeto de ceros se leeria
 * como «el motor estuvo a cero revoluciones», que es otra afirmacion.
 */
export interface CentinelaTelemetria {
  /** El device que la midio. En una ficha forense el dato lleva procedencia. */
  fuente: string | null;
  at: number;
  puntos: number;
  ultima: Record<string, number | string>;
  extremos: Record<string, number>;
}

export interface CentinelaPareja {
  device_id: string;
  nombre: string | null;
  emparejados: number;
  sin_pareja: number;
  divergencia: {
    desde: number;
    hasta: number;
    minutos: number;
    distancia_max_m: number;
  } | null;
}

export interface CentinelaConduccionResponse {
  fecha: string;
  timezone: string;
  /** Solo cuando la unidad trae dos equipos. */
  pareja?: CentinelaPareja | null;
  /** Opcional: las respuestas cacheadas de antes de este campo no la traen. */
  ventana?: CentinelaVentana;
  conduccion: CentinelaConduccion;
}
